export const SCREENSHOT_TARGET_BYTES = 300 * 1024;
/** Worker rejects payment screenshots above this size. */
export const SCREENSHOT_MAX_BYTES = 1.5 * 1024 * 1024;
export const SCREENSHOT_ACCEPT = 'image/jpeg,image/png,image/webp';

const ACCEPTED_TYPES = new Set(SCREENSHOT_ACCEPT.split(','));
const START_MAX_DIMENSION = 1600;
const MIN_DIMENSION = 480;
const QUALITY_STEPS = [0.82, 0.7, 0.58, 0.46];

export function isAcceptedScreenshotType(type: string): boolean {
  return ACCEPTED_TYPES.has(type);
}

/** Scale (width, height) down so the longer edge is at most `maxDimension`, preserving aspect ratio. */
export function fitWithin(width: number, height: number, maxDimension: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxDimension) return { width, height };
  const scale = maxDimension / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

function jpegName(name: string): string {
  const base = name.replace(/\.[^.]+$/, '') || 'payment-screenshot';
  return `${base}.jpg`;
}

/**
 * Re-encode an image as JPEG until it is at most `targetBytes`, shrinking quality first and then
 * dimensions. Files already under the target in an accepted format are returned unchanged.
 */
export async function compressImage(file: File, targetBytes = SCREENSHOT_TARGET_BYTES): Promise<File> {
  if (file.size <= targetBytes && isAcceptedScreenshotType(file.type)) return file;

  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas is not supported in this browser.');

    let maxDimension = START_MAX_DIMENSION;
    let smallest: Blob | null = null;

    while (true) {
      const size = fitWithin(bitmap.width, bitmap.height, maxDimension);
      canvas.width = size.width;
      canvas.height = size.height;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, size.width, size.height);
      context.drawImage(bitmap, 0, 0, size.width, size.height);

      for (const quality of QUALITY_STEPS) {
        const blob = await canvasToBlob(canvas, quality);
        if (!blob) continue;
        if (!smallest || blob.size < smallest.size) smallest = blob;
        if (blob.size <= targetBytes) {
          return new File([blob], jpegName(file.name), { type: 'image/jpeg' });
        }
      }

      const longest = Math.max(size.width, size.height);
      if (longest <= MIN_DIMENSION) break;
      maxDimension = Math.max(MIN_DIMENSION, Math.round(longest * 0.75));
    }

    if (!smallest) throw new Error('Could not process this image.');
    return new File([smallest], jpegName(file.name), { type: 'image/jpeg' });
  } finally {
    bitmap.close();
  }
}
